/* Paws in Clouds — Canvas product photo uploader.
 *
 * Wires up the pic-canvas-photo-upload block:
 *   1. User selects photos via dropzone or file input.
 *   2. We compress client-side (reusing pic-compress-image.js).
 *   3. POST to {proxyPath}/canvas-photos with petName + photos[].
 *   4. Worker uploads to Lark Drive, returns folderUrl.
 *   5. We store folderUrl in hidden input -> becomes Shopify line item property.
 *   6. We block Add-to-Cart until upload is complete.
 */

(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-pic-canvas-upload]').forEach(initUploader);
  });

  function initUploader(root) {
    var proxyPath = root.dataset.proxyPath || '/apps/tribute';
    var maxPhotos = parseInt(root.dataset.maxPhotos || '5', 10);

    var els = {
      dropzone: root.querySelector('.pic-canvas-upload__dropzone'),
      fileInput: root.querySelector('.pic-canvas-upload__file-input'),
      preview: root.querySelector('.pic-canvas-upload__preview'),
      count: root.querySelector('.pic-canvas-upload__count'),
      progress: root.querySelector('.pic-canvas-upload__progress'),
      progressBar: root.querySelector('.pic-canvas-upload__progress-bar'),
      progressText: root.querySelector('.pic-canvas-upload__progress-text'),
      success: root.querySelector('.pic-canvas-upload__success'),
      reset: root.querySelector('.pic-canvas-upload__reset'),
      error: root.querySelector('.pic-canvas-upload__error'),
      folderInput: root.querySelector('[data-pic-canvas-folder]'),
      countInput: root.querySelector('[data-pic-canvas-count]'),
    };

    var state = { files: [], previews: [], uploading: false, uploaded: false };

    /* --- File selection --- */
    els.dropzone.addEventListener('click', function () { els.fileInput.click(); });
    els.dropzone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); els.fileInput.click(); }
    });
    els.dropzone.addEventListener('dragover', function (e) {
      e.preventDefault(); els.dropzone.classList.add('is-dragging');
    });
    els.dropzone.addEventListener('dragleave', function () {
      els.dropzone.classList.remove('is-dragging');
    });
    els.dropzone.addEventListener('drop', function (e) {
      e.preventDefault(); els.dropzone.classList.remove('is-dragging');
      handleFiles(e.dataTransfer.files);
    });
    els.fileInput.addEventListener('change', function (e) { handleFiles(e.target.files); });

    els.reset.addEventListener('click', function () {
      state = { files: [], previews: [], uploading: false, uploaded: false };
      els.folderInput.value = '';
      els.countInput.value = '';
      els.success.hidden = true;
      els.preview.hidden = true;
      els.preview.innerHTML = '';
      els.dropzone.style.display = '';
      updateCount();
      setError('');
    });

    /* --- Cart form submit guard --- */
    var form = root.closest('form');
    if (form) {
      form.addEventListener('submit', function (e) {
        if (!els.folderInput.value) {
          e.preventDefault();
          e.stopImmediatePropagation();
          setError('Please upload at least one photo of your pet before adding to cart.');
          root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true /* capture: run before Shrine's AJAX cart handler */);
    }
    /* Also intercept the Add-to-Cart button click directly (some themes
       wire AJAX cart on click, not form submit). */
    document.querySelectorAll('button[name="add"], .product-form__submit').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        if (!els.folderInput.value) {
          e.preventDefault();
          e.stopImmediatePropagation();
          setError('Please upload at least one photo of your pet before adding to cart.');
          root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true);
    });

    /* --- Helpers --- */
    function handleFiles(fileList) {
      setError('');
      if (state.uploading) return setError('Upload in progress — please wait.');
      var files = Array.from(fileList).slice(0, maxPhotos);
      if (files.length === 0) return;

      state.files = files;
      state.previews = [];
      els.preview.hidden = false;
      els.preview.innerHTML = '';
      els.dropzone.style.display = 'none';

      files.forEach(function (file, i) {
        var img = document.createElement('div');
        img.className = 'pic-canvas-upload__preview-item';
        img.innerHTML = '<div class="pic-canvas-upload__preview-spinner">…</div>';
        els.preview.appendChild(img);

        var reader = new FileReader();
        reader.onload = function (e) {
          img.innerHTML = '<img src="' + e.target.result + '" alt="">';
        };
        reader.readAsDataURL(file);
      });

      updateCount();
      uploadFiles();
    }

    function uploadFiles() {
      var petName = readPetName();
      if (!petName) {
        return setError("Please enter your pet's name first (the field above this).");
      }

      state.uploading = true;
      els.progress.hidden = false;
      els.progressBar.style.width = '5%';
      els.progressText.textContent = 'Compressing photos…';

      compressAll(state.files)
        .then(function (compressed) {
          els.progressBar.style.width = '20%';
          els.progressText.textContent = 'Uploading to our team…';
          return postToWorker(proxyPath + '/canvas-photos', petName, compressed, function (ratio) {
            els.progressBar.style.width = (20 + ratio * 75) + '%';
          });
        })
        .then(function (resp) {
          if (!resp.ok) throw new Error(resp.error || 'Upload failed');
          els.progressBar.style.width = '100%';
          els.progressText.textContent = 'Done!';
          els.folderInput.value = resp.folderUrl;
          els.countInput.value = String(resp.photoCount);
          state.uploading = false;
          state.uploaded = true;
          setTimeout(function () {
            els.progress.hidden = true;
            // Don't show the green success box — the photo previews already
            // signal that upload succeeded. Keep dropzone available so users
            // can drop more / replace images.
            els.success.hidden = true;
            els.dropzone.style.display = '';
            els.dropzone.classList.add('is-replace-mode');
            els.dropzone.querySelector('.pic-canvas-upload__dropzone-text').innerHTML =
              '<strong>Photos ready ✓</strong> · click to replace';
          }, 400);
        })
        .catch(function (err) {
          console.error('[pic-canvas-upload]', err);
          state.uploading = false;
          els.progress.hidden = true;
          els.preview.hidden = true;
          els.preview.innerHTML = '';
          els.dropzone.style.display = '';
          var msg = err && err.message ? String(err.message) : 'Upload failed';
          setError(msg);
        });
    }

    function compressAll(files) {
      if (typeof window.picCompressImage !== 'function') {
        // No compressor loaded — pass files through unchanged.
        return Promise.resolve(files);
      }
      return Promise.all(files.map(function (f) { return window.picCompressImage(f); }));
    }

    function postToWorker(url, petName, files, onProgress) {
      return new Promise(function (resolve, reject) {
        var fd = new FormData();
        fd.append('petName', petName);
        files.forEach(function (f) { fd.append('photos', f, f.name); });

        var xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.upload.onprogress = function (e) {
          if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
        };
        xhr.onload = function () {
          try {
            var data = JSON.parse(xhr.responseText || '{}');
            if (xhr.status >= 200 && xhr.status < 300) resolve(data);
            else reject(new Error(data.error || ('HTTP ' + xhr.status)));
          } catch (parseErr) {
            reject(new Error('Invalid response from server'));
          }
        };
        xhr.onerror = function () { reject(new Error('Network error — check your connection')); };
        xhr.send(fd);
      });
    }

    function readPetName() {
      // Shrine's custom-product-field with field_name "Pet Name" emits this input
      var input = document.querySelector('input[name="properties[Pet Name]"]');
      return input ? input.value.trim() : '';
    }

    function updateCount() {
      if (els.count) els.count.textContent = state.files.length + '/' + maxPhotos;
    }

    function setError(msg) {
      if (!msg) {
        els.error.hidden = true;
        els.error.textContent = '';
        return;
      }
      els.error.hidden = false;
      els.error.textContent = msg;
    }
  }
})();
